import React, { useState, useEffect } from "react";
import { message } from "antd";

import api from "../../services/api";
import country from "../../services/Countrys";
import { stringWithoutCaracteres } from "../../services/stringWithoutCaracteres";

import { exportCSVFile } from "../../helpers/exportCSVFile";
import { removeAccentsAndLowerCase } from "../../helpers/removeAccentsAndCaseSensitive";

import Spinner from "../../components/Spinner";

import CompanyList from "../../containers/CompanyList";
import CompanyForm from "../../containers/CompanyForm";
import PageContainer from "../../containers/PageContainer";

import { User as UserModel } from "../../models/entities/user";
import { Company as CompanyModel, IDepot } from "../../models/entities/company";
import allBrazilianStates from "../../models/enums/allBrazilianStates.json";

import {
  Container,
  Header,
  Content,
  Col,
  Input,
  Select,
  Row,
  SearchIcon,
  Addicon,
  DownloadIcon,
  ContentButton,
} from "./styles";

const Company: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyModel[]>([]);
  const [depots, setDepots] = useState<IDepot[]>([]);
  const [filter, setFilter] = useState<string | undefined>();
  const [filterState, setFilterState] = useState<string | undefined>();
  const [filterCountry, setFilterCountry] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [company, setCompany] = useState<CompanyModel | null>(null);
  const [shouldSearch, setShouldSearch] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingDepots, setLoadingDepots] = useState(true);
  const [formCompanyVisible, setFormCompanyVisible] = useState(false);
  const [visibleCompanyInfoModal, setVisibleCompanyInfoModal] = useState(false);
  const [users, setUsers] = useState<UserModel[]>([]);
  const [profileList, setProfileList] = useState<UserModel[]>([]);

  useEffect(() => {
    const fetchDepots = async () => {
      try {
        setLoadingDepots(true);
        const {
          data: { content },
        } = await api.get("/depot-routech");
        setDepots(content);
      } catch (error: any) {
        message.error("Oops! Não foi possível buscar os depots!");
      } finally {
        setLoadingDepots(false);
      }
    };

    if (shouldSearch) {
      fetchDepots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSearch]);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const {
          data: { content },
        } = await api.get("/company");
        setCompanies(content);
      } catch (error: any) {
        message.error("Oops! Não foi possível buscar os usuários vinculados!");
      } finally {
        setShouldSearch(false);
        setLoading(false);
      }
    };

    if (shouldSearch) {
      fetchCompanies();
    }
  }, [shouldSearch]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const {
          data: { content },
        } = await api.get(`/user`);
        const userFranchisee = content.filter((user) => user?.level === 4);
        setUsers(userFranchisee);
        const {
          data: { content: profile },
        } = await api.get("/user?is_profile=true");

        setProfileList(profile);
      } catch (error: any) {
        message.error("Oops! Não foi possível buscar os usuários!");
      }
    };

    fetchUsers();
  }, []);

  function handleFilterCompanyName(
    pattern?: string,
    stateSigla?: string,
    country?: string,
    company_status?: string
  ) {
    let filteredCompanies = companies;

    if (stateSigla) {
      filteredCompanies = filteredCompanies.filter(
        (entity) =>
          stateSigla === "" ||
          removeAccentsAndLowerCase(entity?.emitente_uf || "") ===
            removeAccentsAndLowerCase(stateSigla)
      );
    }

    if (country) {
      const normalizedCountry = removeAccentsAndLowerCase(country);
      filteredCompanies = filteredCompanies.filter((entity) =>
        removeAccentsAndLowerCase(entity?.country || "")?.includes(
          normalizedCountry
        )
      );
    }

    if (company_status) {
      if (company_status === "true") {
        filteredCompanies = filteredCompanies.filter(
          (entity) => entity.company_status === true
        );
      } else if (company_status === "false") {
        filteredCompanies = filteredCompanies.filter(
          (entity) => entity.company_status === false
        );
      }
    }

    if (pattern) {
      const normalizedPattern = removeAccentsAndLowerCase(pattern);
      const patternOnlyDigits =
        stringWithoutCaracteres(normalizedPattern || "") || "";

      filteredCompanies = filteredCompanies
        .filter((entity) => {
          const normalizedCompanyName = removeAccentsAndLowerCase(
            entity?.company_name || ""
          );
          const normalizedCity = removeAccentsAndLowerCase(entity?.city || "");

          const normalizedCnpjOrRuc = removeAccentsAndLowerCase(
            entity?.cnpj || entity?.ruc || ""
          );

          const cnpjOrRucOnlyDigits =
            stringWithoutCaracteres(normalizedCnpjOrRuc || "") || "";

          const entityId = String(entity?.id || "");
          const entityIdOnlyDigits = stringWithoutCaracteres(entityId) || "";

          return (
            normalizedCompanyName.includes(normalizedPattern) ||
            normalizedCnpjOrRuc.includes(normalizedPattern) ||
            cnpjOrRucOnlyDigits.includes(patternOnlyDigits) ||
            normalizedCity.includes(normalizedPattern) ||
            entityId.includes(normalizedPattern) ||
            entityIdOnlyDigits.includes(patternOnlyDigits)
          );
        })
        .sort((company1, company2) => {
          const sortCompanies = (id: string) => {
            if (id === patternOnlyDigits) return 0;
            if (id.startsWith(patternOnlyDigits)) return 1;
            if (id.includes(patternOnlyDigits)) return 2;
            return 3;
          };

          const sort1 = sortCompanies(String(company1?.id) || "");
          const sort2 = sortCompanies(String(company2?.id) || "");

          if (sort1 !== sort2) return sort1 - sort2;

          return 0;
        });
    }

    return filteredCompanies;
  }

  const downloadCSV = () => {
    const headers = {
      id: "ID DA LOJA",
      name: "NOME DA LOJA",
      franchisee: "ID E PORCENTAGEM",
    };

    const formattedInformations = () => {
      return companies.map((company) => ({
        id: company?.id,
        name: stringWithoutCaracteres(company?.company_name),
        franchisee: company?.companyFranchisee
          ? company.companyFranchisee
              .map(
                (user) =>
                  `ID: ${user?.user_id}, PORCENTAGEM: ${user?.company_percentage}%`
              )
              .join(", ")
          : "",
      }));
    };

    const data = formattedInformations();
    exportCSVFile(headers, data, "Lojas");
  };

  return (
    <PageContainer route="Empresas">
      <Container>
        <Header>
          <Row gutter={6}>
            <Col sm={8} xs={24}>
              <Input
                value={filter}
                prefix={<SearchIcon />}
                placeholder="Buscar id/nome/cnpj/ruc"
                onChange={({ target: { value } }) => setFilter(value)}
                className="search-input"
                style={{ height: "3rem", alignItems: "center" }}
              />
            </Col>

            <Col sm={4} xs={24}>
              <Select
                showSearch
                placeholder="Status da loja"
                onChange={(value) => setFilterStatus(value?.toString())}
                value={filterStatus}
              >
                <Select.Option value="" key="todas">
                  Todas as lojas
                </Select.Option>
                <Select.Option value="true" key="ativa">
                  Ativa
                </Select.Option>
                <Select.Option value="false" key="inativa">
                  Inativa
                </Select.Option>
              </Select>
            </Col>

            <Col sm={6} xs={24}>
              <Select
                showSearch
                placeholder="Selecionar país"
                onChange={(value) => setFilterCountry(value?.toString())}
                value={filterCountry}
              >
                {country.map((country) => (
                  <Select.Option value={country.codigo} key={country.codigo}>
                    {country.nome}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col sm={6} xs={24}>
              <Select
                placeholder="Filtro de estado"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
                onChange={(value) => setFilterState(value?.toString())}
              >
                {allBrazilianStates.map((state) => (
                  <Select.Option value={state.sigla} key={state.sigla}>
                    {state.nome}
                  </Select.Option>
                ))}
              </Select>
            </Col>
          </Row>

          <Row>
            <ContentButton>
              <Col sm={7} xs={12}>
                <button onClick={() => downloadCSV()} className="btnCsv">
                  <DownloadIcon />
                  <span>Download CSV</span>
                </button>
              </Col>
              <Col sm={7} xs={12}>
                <button
                  onClick={() => {
                    setCompany(null);
                    setFormCompanyVisible(true);
                  }}
                  className="btnModal"
                >
                  <Addicon />
                  <span>Nova Empresa</span>
                </button>
              </Col>
            </ContentButton>
          </Row>
        </Header>
        <Content>
          {loading || loadingDepots ? (
            <Spinner />
          ) : (
            <CompanyList
              setVisible={setFormCompanyVisible}
              setVisibleCompanyInfoModal={setVisibleCompanyInfoModal}
              company={company}
              data={handleFilterCompanyName(
                filter,
                filterState,
                filterCountry,
                filterStatus
              )}
              setCompany={setCompany}
              setShouldSearch={setShouldSearch}
              profileList={profileList}
            />
          )}
        </Content>
        <CompanyForm
          setVisible={setFormCompanyVisible}
          visible={formCompanyVisible}
          company={company}
          setCompany={setCompany}
          setShouldSearch={setShouldSearch}
          users={users}
          visibleCompanyInfoModal={visibleCompanyInfoModal}
          setVisibleCompanyInfoModal={setVisibleCompanyInfoModal}
          depots={depots}
          companies={companies}
        />
      </Container>
    </PageContainer>
  );
};

export default Company;
